'use client';

import { useEffect, useState } from 'react';
import { useReducedMotion } from '@/components/motion/useReducedMotion';
import { HEADLINES, HEADLINE_INTERVAL_MS } from '@/lib/data/headlines';

/**
 * The hero headline, cycling through a handful of phrases.
 *
 * The first phrase IS the h1. The other phrases are aria-hidden siblings
 * stacked in the same grid cell, so they can cross-fade with it, but they are
 * not inside the heading: the h1's text content is one stable phrase for
 * crawlers, reader mode, copy and paste, and assistive technology. When a
 * later phrase is showing, the h1 is faded to transparent but still there.
 *
 * All phrases stay mounted so the outgoing and incoming ones can dissolve
 * through each other (destroying one as the next appears reads as a cut), and
 * the shared cell fixes the block's height so the page below never shifts.
 *
 * The visible and hidden states are plain CSS, so the correct phrase shows in
 * the static HTML and for anyone without JavaScript. Rotation stops entirely
 * under prefers-reduced-motion; a visitor who sets it gets the phrase that
 * names the shop.
 *
 * The phrases play through once and stop on the first again. Content that
 * keeps changing by itself has to offer a pause control (WCAG 2.2.2); a single
 * cycle that ends where it began needs none, and a reader who arrives late
 * still gets to read every phrase once.
 */
export function RotatingHeadline({ className = '' }: { className?: string }) {
  const [index, setIndex] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Wait until the preference is known; never start on a reduced-motion device.
    if (HEADLINES.length < 2 || reducedMotion !== false) return;
    const timer = setInterval(() => {
      setIndex((current) => {
        const next = (current + 1) % HEADLINES.length;
        if (next === 0) clearInterval(timer);
        return next;
      });
    }, HEADLINE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reducedMotion]);

  const [first, ...rest] = HEADLINES;
  if (!first) return null;

  return (
    <div className={`phrase-stack ${className}`}>
      <h1 className="phrase" data-active={index === 0 ? 'true' : undefined}>
        <span className="block">{first[0]}</span>
        <span className="block">{first[1]}</span>
      </h1>
      {rest.map((phrase, offset) => (
        <span
          key={phrase.join(' ')}
          aria-hidden="true"
          className="phrase"
          data-active={index === offset + 1 ? 'true' : undefined}
        >
          <span className="block">{phrase[0]}</span>
          <span className="block">{phrase[1]}</span>
        </span>
      ))}
    </div>
  );
}
